import {
  CalendarDate,
  CalendarDateTime,
  getLocalTimeZone,
  parseAbsolute,
} from '@internationalized/date';
import { isNil, isString } from 'lodash';
import moment from 'moment';

export function today() {
  return new Date();
}

export function now() {
  return today().getTime();
}

export function isSameDay(a: Date | null | undefined, b: Date | null | undefined): boolean {
  return !isNil(a) && !isNil(b) && moment(a).isSame(b, 'day');
}

export function formatDate(date: string, hideYear: boolean = false) {
  if (hideYear || moment().isSame(date, 'year')) {
    return moment(date).format('MMM D');
  }
  return formatDateWithYear(date);
}

export function formatDateWithYear(date: string) {
  return moment(date).format('MMM D, YYYY');
}

export function formatTime(date: string, abbreviateWholeHours: boolean = true) {
  if (abbreviateWholeHours && moment(date).minutes() === 0) {
    return moment(date).format('ha');
  }
  return moment(date).format('h:mma');
}

export function formatRelativeTime(date: string) {
  const m = moment(date);
  const tf = m.minutes() === 0 ? 'ha' : 'h:mma';
  return m.calendar(null, {
    lastDay: `[Yesterday,] ${tf}`,
    sameDay: `[Today,] ${tf}`,
    nextDay: `[Tomorrow,] ${tf}`,
    lastWeek: `[Last] dddd, ${tf}`,
    nextWeek: `dddd, ${tf}`,
    sameElse: `MMM D, ${tf}`,
  });
}

export function formatDateTime(date: string) {
  return [formatDate(date), formatTime(date)].join(', ');
}

export function formatDateTimeWithYear(date: string) {
  return [formatDateWithYear(date), formatTime(date)].join(', ');
}

function formatDateRangeStartDate(startDate: moment.Moment, endDate: moment.Moment): string {
  const showYear = !startDate.isSame(endDate, 'year');
  const format = `MMM D${showYear ? ', YYYY' : ''}`;
  return startDate.format(format);
}

function formatDateRangeEndDate(startDate: moment.Moment, endDate: moment.Moment): string {
  const showYear = !startDate.isSame(endDate, 'year') || !moment().isSame(endDate, 'year');
  const showMonth = !startDate.isSame(endDate, 'month');
  const format = `${showMonth ? 'MMM ' : ''}D${showYear ? ', YYYY' : ''}`;
  return endDate.format(format);
}

export function formatDateRange(start: string, end: string): string {
  const startDate = moment(start);
  const endDate = moment(end);

  const from = formatDateRangeStartDate(startDate, endDate);
  const to = formatDateRangeEndDate(startDate, endDate);
  return `${from} – ${to}`;
}

export function formatTimeRange(start: string, end: string): string {
  return `${formatTime(start)} - ${formatTime(end)}`;
}

export function formatDateTimeRange(start: string, end: string): string {
  const { from, to } = getFormattedDateTimeRangeParts(start, end);
  return `${from} – ${to}`;
}

export function getFormattedDateTimeRangeParts(
  start: string,
  end: string,
): { from: string; to: string } {
  const s = moment(start);
  const e = moment(end);
  const isDifferentYear = !moment().isSame(s, 'year') || !moment().isSame(e, 'year');

  const showStartYear = !e.isSame(s, 'year') || isDifferentYear;
  const showEndYear = !e.isSame(s, 'year') || isDifferentYear;
  const showMonthAndDay = !s.isSame(e, 'day') || isDifferentYear;

  const fromDateFormat = `MMM D${showStartYear ? ', YYYY' : ''}`;
  const toDateFormat = `${showMonthAndDay ? 'MMM D' : ''}${showEndYear ? ', YYYY' : ''}`;

  const hasMinutes = s.minutes() !== 0 || e.minutes() !== 0;
  const abbreviateWholeHours = !hasMinutes;

  const from = `${s.format(fromDateFormat)}, ${formatTime(start, abbreviateWholeHours)}`;
  const to = !!toDateFormat
    ? `${e.format(toDateFormat)}, ${formatTime(end, abbreviateWholeHours)}`
    : formatTime(end, abbreviateWholeHours);
  return { from, to };
}

export function getPartialFormattedDateTimeRangeParts(
  start: string | null | undefined,
  end: string | null | undefined,
): { from?: string; to?: string } {
  if (!isNil(start) && !isNil(end)) {
    return getFormattedDateTimeRangeParts(start, end);
  }
  if (!isNil(start)) {
    return { from: formatDateTime(start) };
  }
  if (!isNil(end)) {
    return { to: formatDateTime(end) };
  }
  return { from: undefined, to: undefined };
}

const dateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z$/;

export function isDateString(date: unknown): boolean {
  return isString(date) && dateRegex.test(date);
}

export function toLocalCalendarDateTime(isoString: string): CalendarDateTime {
  // Parse to ZonedDateTime in the viewer's local timezone
  const zonedDate = parseAbsolute(isoString, getLocalTimeZone());

  // Convert to CalendarDateTime
  return new CalendarDateTime(
    zonedDate.year,
    zonedDate.month,
    zonedDate.day,
    zonedDate.hour,
    zonedDate.minute,
    zonedDate.second,
    zonedDate.millisecond,
  );
}

export function toISOString(calendarDateTime: CalendarDateTime): string {
  const localDate = calendarDateTime.toDate(getLocalTimeZone());
  return localDate.toISOString();
}

export function toCalendarDate(date: string | number | Date): CalendarDate {
  // Read the calendar day in the viewer's local timezone, mirroring how dates
  // are saved (local midnight -> UTC). Using the UTC date portion here would
  // shift the displayed day back for viewers east of UTC (e.g. Asia/Pacific).
  const d = new Date(date);
  return new CalendarDate(d.getFullYear(), d.getMonth() + 1, d.getDate());
}
